import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useSubscription, SubscriptionTier } from '@/hooks/useSubscription';
import { Loader2, ShieldAlert, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface RoleProtectedRouteProps {
  children: ReactNode;
  allowedRoles: string[];
  requiredTier?: SubscriptionTier; // Optionnel : pour restreindre à un niveau d'abonnement
  redirectTo?: string;
}

/**
 * Composant de protection de route avancé
 * Vérifie l'authentification, les rôles Firebase et le tier d'abonnement
 */
export function RoleProtectedRoute({
  children,
  allowedRoles,
  requiredTier,
  redirectTo = '/login'
}: RoleProtectedRouteProps) {
  const { user, loading: authLoading } = useAuth();
  const { tier, loading: subLoading, subscribed } = useSubscription();
  const location = useLocation();

  // 1. État de chargement global (Auth + Abonnement)
  if (authLoading || subLoading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-slate-50">
        <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
        <p className="text-sm font-medium text-slate-500 animate-pulse">
          Vérification des accès en cours...
        </p>
      </div>
    );
  }

  // 2. Redirection si non authentifié
  if (!user) {
    // On sauvegarde l'URL actuelle pour y revenir après le login
    sessionStorage.setItem('intended_destination', location.pathname);
    return <Navigate to={redirectTo} state={{ from: location }} replace />;
  }

  // 3. Vérification des Rôles
  const userRoles = (user as any).roles || [];
  const hasRoleAccess = userRoles.some((role: string) => allowedRoles.includes(role));

  // 4. Vérification optionnelle du Tier d'abonnement (si fourni)
  // On laisse passer si c'est un Admin (super-pouvoir)
  const isAdmin = userRoles.includes('admin');
  let hasSubscriptionAccess = true;

  if (requiredTier && !isAdmin) {
    // Logique de hiérarchie simple (ajustez selon votre business logic)
    const tierWeights: Record<string, number> = {
      'visitor': 0,
      'essential': 1,
      'essential-plus': 2,
      'pro': 3,
      'byod': 4
    };
    
    const userTierWeight = tierWeights[tier || 'visitor'] || 0;
    const requiredWeight = tierWeights[requiredTier] || 0;
    
    hasSubscriptionAccess = userTierWeight >= requiredWeight;
  }

  // 5. Rendu si accès refusé (Rôle ou Abonnement)
  if (!hasRoleAccess || !hasSubscriptionAccess) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-6 bg-slate-50">
        <div className="bg-white p-8 rounded-2xl shadow-xl border border-slate-200 max-w-md w-full text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-destructive/10 text-destructive mb-6">
            <ShieldAlert className="h-10 w-10" />
          </div>
          
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Accès Restreint</h1>
          
          <div className="space-y-4 mb-8">
            {!hasRoleAccess ? (
              <p className="text-slate-600 text-sm">
                Votre rôle actuel (<span className="font-mono font-bold text-primary">{userRoles.join(', ') || 'aucun'}</span>) 
                ne permet pas d'accéder à cette section réservée aux : 
                <span className="font-semibold text-slate-900"> {allowedRoles.join(', ')}</span>.
              </p>
            ) : (
              <p className="text-slate-600 text-sm">
                Cette fonctionnalité nécessite un abonnement <span className="font-bold text-primary uppercase">{requiredTier}</span>. 
                Votre niveau actuel est <span className="font-bold text-slate-900 uppercase">{tier || 'visitor'}</span>.
              </p>
            )}
          </div>

          <div className="flex flex-col gap-3">
            {!hasSubscriptionAccess && (
              <Button onClick={() => { window.location.href = '/pricing'; }} className="w-full bg-primary font-bold">
                Mettre à niveau mon offre
              </Button>
            )}
            <Button 
              variant="outline" 
              onClick={() => window.history.back()} 
              className="w-full flex gap-2 items-center justify-center"
            >
              <ArrowLeft className="h-4 w-4" /> Retour
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // 6. Succès : Rendu du composant enfant
  return <>{children}</>;
}